import React, { Fragment, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { View } from 'react-native';
import { styled, useBreakpointIndex } from 'dripsy';
import { useNavigation } from '@react-navigation/native';

import { SectionItemListTypeEnum } from '@constants';
import { Tutorial } from '@models/Tutorials/TutorialModel';
import { Line, Separator, TagHorizontalList } from '@components';
import { ActionPanel } from './ActionPanel';
import { ViewsInfo } from './ViewsInfo';
import { HubNavigatorNavigationPropType, screens } from '@navigation';
import type { Tag } from '@models/Tags/TagModel';
import { useAsyncInit } from '@hooks';
import { Course } from '@models/Courses/CourseModel';

const Container = styled(View)({
  flexDirection: ['column', 'row'],
  justifyContent: ['flex-start', 'space-between'],
});

export interface IInfoPanelProps {
  tutorial: Tutorial;
  course?: Course;
}

type NavigationPropType = HubNavigatorNavigationPropType<'Tutorial'>;

export const InfoPanel = observer<IInfoPanelProps>(
  ({ tutorial, course }) => {
    const bIndex = useBreakpointIndex();
    const nav = useNavigation<NavigationPropType>();

    useAsyncInit(async () => {
      try {
        await tutorial.tags.fetch.run();
      } catch (error) {
        // Do nothing
      }
    }, [tutorial.id]);

    const navigateToTaggedItems = useCallback((item: Tag) => {
      nav.push(screens.Items, {
        type: SectionItemListTypeEnum.Tag,
        id: item.id,
        name: item.name,
      });
    }, []);

    if (bIndex > 0) {
      return (
        <View>
          <Container>
            <ViewsInfo tutorial={tutorial} />
            <ActionPanel tutorial={tutorial} course={course} />
          </Container>
          {!tutorial.tags.isEmpty && (
            <Fragment>
              <Separator size="$5" />
              <TagHorizontalList
                onPress={navigateToTaggedItems}
                data={tutorial.tags.asArray}
              />
            </Fragment>
          )}
        </View>
      );
    }

    return (
      <Container>
        <ActionPanel tutorial={tutorial} course={course} />
        <Separator size="$7" />
        <Line />
        {!tutorial.tags.isEmpty && (
          <Fragment>
            <Separator size="$5" />
            <TagHorizontalList
              onPress={navigateToTaggedItems}
              data={tutorial.tags.asArray}
            />
          </Fragment>
        )}
        <Separator size="$7" />
        <ViewsInfo tutorial={tutorial} />
      </Container>
    );
  },
);
